<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { refDebounced } from '@vueuse/core'
import moment from 'moment'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    name: 'Session Activity',
    subject: "care_visit.menu",
    action: "session",
  },
})

const unitId = computed(() => cookieRef('selectedUnit').value)

// Search and filter state
const searchQuery = ref('')
const debouncedSearchQuery = refDebounced(searchQuery, 1000)

// Session status filter (All, Active, Complete)
const sessionStatus = ref('all')

// Default date range - Last 30 days
const defaultFromDate = moment().subtract(30, 'days').format('YYYY-MM-DD')
const defaultToDate = moment().format('YYYY-MM-DD')

// Date range state
const createdAtDateRange = ref(`${defaultFromDate} to ${defaultToDate}`)
const fromDate = ref(defaultFromDate)
const toDate = ref(defaultToDate)
const initialLoading = ref(true)

// Date Range Options
const dateRangeOptions = ref({
  mode: 'range',
  maxDate: null,
  dateFormat: 'Y-m-d',
  enableTime: false,
  defaultDate: [defaultFromDate, defaultToDate],
  onChange: (selectedDates, dateStr) => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : fromDate.value
    }
  },
})

// Pagination and sorting state
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref('desc')
const orderBy = ref('created_at')

// Table headers
const headers = [
  // { title: 'Actions', key: 'actions', sortable: false },
  { title: 'Pass Code', key: 'pass_code' },
  { title: 'Visitor', key: 'visitor' },
  { title: 'Patient', key: 'patient' },
  { title: 'Check In', key: 'checked_in_at' },
  { title: 'Check Out', key: 'checked_out_at' },
  { title: 'Duration', key: 'duration', sortable: false },
  { title: 'Rating', key: 'visit_rating' },
]

// Computed search params for API
const computedFilters = computed(() => {
  const params = {
    unit_id: unitId.value,
    created_at: [fromDate.value, toDate.value],
  }

  // Add status filter based on selection
  if (sessionStatus.value === 'active') {
    params.checked_out_at = 'null'
  } else if (sessionStatus.value === 'complete') {
    params.checked_in_at = 'not-null'
    params.checked_out_at = 'not-null'
  }

  return params
})

const debouncedFilters = refDebounced(computedFilters, 500)


// Fetch sessions using useApi
const {
  isFetching: isLoadingSessions,
  data: sessionsData,
  execute: fetchSessions,
} = useApi(
  createUrl('/care-visit/sessions', {
    query: {
      search: debouncedFilters,
      q: debouncedSearchQuery,
      paged: 1,
      itemsPerPage,
      page: pageNumber,
      sort: sortBy,
      order_by: orderBy,
    },
  }),
)

// Watch for debounced search query changes to reset pagination
// watch(debouncedSearchQuery, () => {
//   pageNumber.value = 1
// })

// // Watch for filter changes to reset pagination (refetch happens automatically)
// watch(computedFilters, () => {
//   pageNumber.value = 1
// }, {
//   deep: true,
// })

// Computed session data
const sessions = computed(() => sessionsData.value?.data ?? sessionsData.value ?? [])

const totalSessions = computed(() => {
  return sessionsData.value?.meta?.total ?? sessionsData.value?.total ?? 0
})

// Update table options
const updateOptions = options => {
  pageNumber.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Refresh sessions
const refreshSessions = () => {
  fetchSessions()
}

// Export loading state
const exportLoading = ref(false)

// Export sessions
const exportSessions = async () => {
  exportLoading.value = true

  const url = createUrl('/care-visit/sessions/export', {
    query: {
      q: searchQuery.value,
      search: computedFilters.value,
      sort: sortBy.value,
      order_by: orderBy.value,
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(error => {
    console.error('Error exporting sessions:', error)
  })

  exportLoading.value = false
}

// Format datetime helper
const formatDateTime = timestamp => {
  if (!timestamp) return 'N/A'

  // return moment(timestamp).format('MMM DD, YYYY hh:mm A')
  // return moment(timestamp).format('hh:mm A, MMM DD')
  return moment(timestamp).format('hh:mm A, Do MMM')
}

// Calculate visit duration
const getVisitDuration = session => {
  if (!session.checked_in_at) return '-'

  const checkIn = moment(session.checked_in_at)
  const checkOut = session.checked_out_at ? moment(session.checked_out_at) : moment()

  const duration = moment.duration(checkOut.diff(checkIn))
  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }

  return `${minutes}m`
}

// Get session status color for avatar
const getSessionStatusColor = session => {
  if (!session.checked_out_at) return 'success'

  return 'default'
}

// View session details
const viewSession = session => {
  // TODO: Implement view session details
  console.log('View session:', session)
}

// End session
const endSession = session => {
  // TODO: Implement end session
  console.log('End session:', session)
}

watch(sessionsData, () => {
  initialLoading.value = false
})
</script>

<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-center gap-4 mb-6">
      <div>
        <h4 class="text-h4">
          Session Activity
        </h4>
        <p class="text-body-2 text-disabled mb-0">
          View and manage visitor session activity
        </p>
      </div>
    </div>

    <!-- Filters and Table Card -->
    <VCard
      variant="flat"
      class="border"
    >
      <!-- Search and Filters -->
      <VCardText class="d-flex flex-column gap-4">
        <!-- Row 1: Search and Date Picker -->
        <div class="d-flex flex-wrap align-center gap-4">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search by visitor name, patient name..."
            density="compact"
            clearable
            class="flex-grow-1"
            style="min-inline-size: 300px;"
          />
          <AppDateTimePicker
            v-model="createdAtDateRange"
            placeholder="Select Date Range"
            :config="dateRangeOptions"
            :disabled="isLoadingSessions"
            style="min-inline-size: 250px;"
          />
        </div>

        <!-- Row 2: Status Toggle and Actions -->
        <div class="d-flex flex-wrap align-center gap-4">
          <VBtnToggle
            v-model="sessionStatus"
            color="primary"
            variant="outlined"
            density="comfortable"
            divided
          >
            <VBtn value="all">
              All
            </VBtn>
            <VBtn value="active">
              Active Session
            </VBtn>
            <VBtn value="complete">
              Complete Session
            </VBtn>
          </VBtnToggle>
          <VSpacer />

          <VBtn
            variant="tonal"
            color="primary"
            prepend-icon="tabler-download"
            :loading="exportLoading"
            @click="exportSessions"
          >
            Export Data
          </VBtn>

          <VBtn
            color="secondary"
            variant="tonal"
            prepend-icon="tabler-refresh"
            :loading="isLoadingSessions"
            @click="refreshSessions"
          >
            Refresh
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- Sessions Table -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="pageNumber"
        :items="sessions"
        :items-length="totalSessions"
        :headers="headers"
        fixed-header
        class="text-no-wrap"
        :loading="initialLoading || isLoadingSessions"
        @update:options="updateOptions"
      >
        <template #item.pass_code="{ item }">
          {{ item.visitor_qr_code.code }}
        </template>

        <!-- Visitor Column -->
        <template #item.visitor="{ item }">
          {{ item.visitor?.name ?? 'N/A' }}
          <VTooltip
            activator="parent"
            location="bottom"
          >
            <div class="text-capitalize">
              Pass Type: {{ item.visitor_type ?? 'N/A' }}
            </div>
            <div>Phone No: {{ `${item.visitor?.country_code}${item.visitor?.phone}` }}</div>
          </VTooltip>
        </template>

        <!-- Patient Column -->
        <template #item.patient="{ item }">
          {{ item.episode?.customer?.name ?? 'N/A' }}
          <VTooltip
            activator="parent"
            location="bottom"
          >
            <div>Episode Code: {{ item.episode?.code ?? 'N/A' }}</div>
            <div>UHID: {{ item.episode?.customer?.uhid ?? 'N/A' }}</div>
            <div>Location: {{ item.episode?.location?.name ?? 'N/A' }}</div>
          </VTooltip>
        </template>

        <!-- Check In Column -->
        <template #item.checked_in_at="{ item }">
          {{ formatDateTime(item.checked_in_at) }}
          <VTooltip
            activator="parent"
            location="bottom"
          >
            <div>Kiosk: {{ item.checked_in_access_log?.kiosk?.name ?? 'N/A' }}</div>
            <div>Location: {{ item.checked_in_access_log?.location?.name ?? 'N/A' }}</div>
          </VTooltip>
        </template>

        <!-- Check Out Column -->
        <template #item.checked_out_at="{ item }">
          {{ formatDateTime(item.checked_out_at) }}
          <VTooltip
            activator="parent"
            location="bottom"
          >
            <div>Kiosk: {{ item.checked_out_access_log?.kiosk?.name ?? 'N/A' }}</div>
            <div>Location: {{ item.checked_out_access_log?.location?.name ?? 'N/A' }}</div>
          </VTooltip>
        </template>

        <!-- Duration Column -->
        <template #item.duration="{ item }">
          {{ item.visit_duration_formatted ?? 'N/A' }}
        </template>

        <!-- Rating Column -->
        <template #item.visit_rating="{ item }">
          <div v-if="item.visit_rating">
            <VTooltip
              activator="parent"
              location="bottom"
            >
              <template #activator="{ props }">
                <div class="d-flex align-center gap-1">
                  <div class="font-weight-medium">
                    {{ item.visit_rating }}
                  </div>
                  <VIcon
                    icon="tabler-star-filled"
                    size="18"
                    color="warning"
                  />
                </div>
              </template>

              <div>Comment: {{ item.visit_feedback }}</div>
            </VTooltip>
          </div>
          <div v-else>
            N/A
          </div>
        </template>

        <!-- Actions Column -->
        <template #item.actions="{ item }">
          <IconBtn @click="viewSession(item)">
            <VIcon icon="tabler-eye" />
            <VTooltip
              activator="parent"
              location="bottom"
            >
              View Details
            </VTooltip>
          </IconBtn>
          <!--
            <IconBtn
            v-if="!item.checked_out_at"
            color="error"
            @click="endSession(item)"
            >
            <VIcon icon="tabler-logout" />
            <VTooltip
            activator="parent"
            location="bottom"
            >
            End Session
            </VTooltip>
            </IconBtn> 
          -->
        </template>

        <!-- Empty state -->
        <template #no-data>
          <div class="text-center pt-6 pb-4">
            <VIcon
              icon="tabler-inbox"
              size="48"
              class="text-disabled mb-2"
            />
            <div class="text-body-1 text-disabled">
              No sessions found
            </div>
          </div>
        </template>
      </VDataTableServer>
    </VCard>
  </div>
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
